/**
 * Address utilities for Google Places API integration
 */

/**
 * Fetches address suggestions using Google Places Autocomplete
 * @param {string} query - Search query
 * @returns {Promise<Array>} Array of address suggestions
 */
export async function fetchAddressSuggestions(query) {
  if (!query || query.trim().length < 2) return [];
  if (typeof window === 'undefined' || !window.google?.maps?.places) {
    console.warn('Google Maps API not loaded yet');
    return [];
  }

  try {
    const autocompleteService = new window.google.maps.places.AutocompleteService();
    const placesService = new window.google.maps.places.PlacesService(
      document.createElement('div')
    );

    return new Promise((resolve) => {
      autocompleteService.getPlacePredictions(
        {
          input: query,
          componentRestrictions: { country: 'IN' },
          types: ['geocode', 'establishment'],
        },
        async (predictions, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
            resolve([]);
            return;
          }

          const results = await Promise.all(
            predictions.slice(0, 6).map(
              (prediction) =>
                new Promise((res) => {
                  placesService.getDetails(
                    { placeId: prediction.place_id },
                    (place, detailStatus) => {
                      if (
                        detailStatus === window.google.maps.places.PlacesServiceStatus.OK &&
                        place
                      ) {
                        res({
                          label: prediction.description,
                          address: place.formatted_address || prediction.description,
                          latitude: place.geometry?.location?.lat() || 0,
                          longitude: place.geometry?.location?.lng() || 0,
                          placeId: prediction.place_id,
                        });
                      } else {
                        res(null);
                      }
                    }
                  );
                })
            )
          );

          resolve(results.filter(Boolean));
        }
      );
    });
  } catch (err) {
    console.error('fetchAddressSuggestions error:', err);
    return [];
  }
}

/**
 * Gets current location address using reverse geocoding
 * @param {string} apiKey - Google Maps API key
 * @returns {Promise<Object>} Address object with label, address, latitude, longitude, placeId
 */
export async function getCurrentLocationAddress(apiKey) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`
          );
          const data = await response.json();
          if (data.results && data.results[0]) {
            const place = data.results[0];
            resolve({
              label:
                place.formatted_address || place.formatted_address || `${latitude}, ${longitude}`,
              address: place.formatted_address || `${latitude}, ${longitude}`,
              latitude,
              longitude,
              placeId: place.place_id || null,
              raw: place,
            });
          } else {
            resolve({
              label: `${latitude}, ${longitude}`,
              address: `${latitude}, ${longitude}`,
              latitude,
              longitude,
              placeId: null,
            });
          }
        } catch (err) {
          resolve({
            label: `${latitude}, ${longitude}`,
            address: `${latitude}, ${longitude}`,
            latitude,
            longitude,
            placeId: null,
          });
        }
      },
      (err) => {
        reject(err);
      }
    );
  });
}
